import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context.js';
import { DEMO_TAGS, toEnvelope, useDemoOrLive } from '../demo/index.js';

/**
 * Cross-slice admin metadata reads. Teams + tags ride together
 * because they're administrative metadata both consumed across the
 * dashboard, governance, and scenario detail surfaces.
 */

type Row = Record<string, unknown>;

/**
 * Engine envelopes:
 *   /engine/v1/teams-info  → { data: TeamInfo[] }
 *   /engine/v1/tags        → { tags: Tag[] }
 * Hooks unwrap so consumers see a bare array.
 */

export function useTeamsInfo(): { data?: Row[]; isLoading: boolean; error: Error | null } {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['teams-info', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ data: Row[] }>('/v1/teams-info').then((r) => r.data),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
  return { data: q.data, isLoading: q.isLoading, error: (q.error as Error) ?? null };
}

export function useTags(): { data?: Row[]; isLoading: boolean; error: Error | null } {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['tags', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ tags: Row[] }>('/v1/tags').then((r) => r.tags),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
  // Demo mode: bundled 6-tag catalog (`DEMO_TAGS`) so the
  // presentation list's group-by-tag layout + the scenario-detail
  // modal's Tags box have content without hitting the engine.
  const env = useDemoOrLive(DEMO_TAGS as Row[], toEnvelope(q));
  return { data: env.data, isLoading: env.isLoading, error: env.error };
}

export interface CreateTagInput {
  /** Engine schema field name (legacy parity) — the legacy
   *  `scenario_tag` table column is `title`, not `name`. */
  title: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  /** Optional initial scenario set — when provided, the engine
   *  inserts the tag and atomically attaches it to these scenarios
   *  in one round-trip (legacy tag-upsert behaviour). */
  scenarioIds?: string[];
}

export function useCreateTag(): {
  create: (input: CreateTagInput) => Promise<{ tagId: number }>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: CreateTagInput) => api.post<{ tagId: number }>('/v1/tags', input),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ['tags', session?.licenseId ?? 'anon'] });
      toast.success(`Tag "${input.title}" added`);
    },
    onError: (err: Error) => toast.error(`Could not add tag: ${err.message}`),
  });
  return {
    create: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useUpdateTag(): {
  update: (input: {
    tagId: number;
    title: string;
    description?: string | null;
    color?: string | null;
    icon?: string | null;
  }) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({
      tagId,
      ...rest
    }: {
      tagId: number;
      title: string;
      description?: string | null;
      color?: string | null;
      icon?: string | null;
    }) => api.put<unknown>(`/v1/tags/${encodeURIComponent(tagId)}`, rest),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ['tags', session?.licenseId ?? 'anon'] });
      toast.success(`Tag "${input.title}" updated`);
    },
    onError: (err: Error) => toast.error(`Could not update tag: ${err.message}`),
  });
  return {
    update: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useDeleteTag(): {
  remove: (tagId: string) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (tagId: string) => api.delete<unknown>(`/v1/tags/${encodeURIComponent(tagId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tags', session?.licenseId ?? 'anon'] });
      toast.success('Tag deleted');
    },
    onError: (err: Error) => toast.error(`Could not delete tag: ${err.message}`),
  });
  return {
    remove: (tagId) => m.mutateAsync(tagId),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}
